(() => {
  const SELECTOR = '.hardrip-variant-picker';

  const TONES = [
    {
      key: 'rose-gold',
      aliases: ['oro rosa', 'oro rosato', 'rose gold', 'rosegold', 'gold rose', 'rosato'],
      base: '#b76e79',
      light: '#f0c7c9',
      dark: '#8d4e5b',
      text: '#171014',
      shadow: 'rgba(183, 110, 121, 0.34)',
    },
    {
      key: 'yellow-gold',
      aliases: ['oro giallo', 'yellow gold', 'gold yellow', 'oro', 'gold', 'dorato'],
      base: '#d4af37',
      light: '#fff0a6',
      dark: '#9f7720',
      text: '#171207',
      shadow: 'rgba(212, 175, 55, 0.34)',
    },
    {
      key: 'silver',
      aliases: [
        'argento 925',
        'argento',
        'silver',
        'acciaio inossidabile',
        'stainless steel',
        'acciaio',
        'steel',
        'platino',
        'platinum',
        'titanio',
        'titanium',
      ],
      base: '#b8bec7',
      light: '#f7f9fb',
      dark: '#747c87',
      text: '#0f1115',
      shadow: 'rgba(196, 202, 211, 0.28)',
    },
    {
      key: 'gunmetal',
      aliases: ['canna di fucile', 'gunmetal', 'grigio scuro', 'dark grey', 'dark gray'],
      base: '#555965',
      light: '#a7acb7',
      dark: '#292c34',
      text: '#ffffff',
      shadow: 'rgba(86, 91, 104, 0.38)',
    },
    {
      key: 'champagne',
      aliases: ['champagne', 'sabbia', 'beige'],
      base: '#cdb98c',
      light: '#fff2c9',
      dark: '#9a8155',
      text: '#17130a',
      shadow: 'rgba(205, 185, 140, 0.3)',
    },
    {
      key: 'copper',
      aliases: ['rame', 'copper', 'bronzo', 'bronze'],
      base: '#b87333',
      light: '#f2b37a',
      dark: '#75401c',
      text: '#160d07',
      shadow: 'rgba(184, 115, 51, 0.34)',
    },
    {
      key: 'white',
      aliases: ['bianco', 'white'],
      base: '#f7f7f7',
      light: '#ffffff',
      dark: '#c7c7cc',
      text: '#0b0b0d',
      shadow: 'rgba(255, 255, 255, 0.24)',
    },
    {
      key: 'black',
      aliases: ['nero', 'black', 'onyx', 'onice'],
      base: '#17171c',
      light: '#666672',
      dark: '#050507',
      text: '#ffffff',
      shadow: 'rgba(0, 0, 0, 0.52)',
    },
    {
      key: 'purple',
      aliases: ['viola hardrip', 'hardrip purple', 'viola', 'purple', 'lilla', 'lilac'],
      base: '#5f3c96',
      light: '#a98bd4',
      dark: '#3d236c',
      text: '#ffffff',
      shadow: 'rgba(95, 60, 150, 0.4)',
    },
    {
      key: 'red',
      aliases: ['rosso', 'red', 'rubino', 'ruby', 'bordeaux', 'burgundy'],
      base: '#c82f45',
      light: '#ff8c9c',
      dark: '#761827',
      text: '#ffffff',
      shadow: 'rgba(200, 47, 69, 0.38)',
    },
    {
      key: 'blue',
      aliases: ['blu', 'blue', 'zaffiro', 'sapphire', 'azzurro'],
      base: '#2768c7',
      light: '#79b1ff',
      dark: '#12366f',
      text: '#ffffff',
      shadow: 'rgba(39, 104, 199, 0.38)',
    },
    {
      key: 'green',
      aliases: ['verde', 'green', 'smeraldo', 'emerald'],
      base: '#27845d',
      light: '#78d8ae',
      dark: '#114b34',
      text: '#ffffff',
      shadow: 'rgba(39, 132, 93, 0.36)',
    },
    {
      key: 'pink',
      aliases: ['rosa', 'pink', 'fucsia', 'fuchsia'],
      base: '#d04f91',
      light: '#ffabd4',
      dark: '#7c2855',
      text: '#ffffff',
      shadow: 'rgba(208, 79, 145, 0.36)',
    },
  ];

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[’']/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const aliasCandidates = TONES.flatMap((tone) =>
    tone.aliases.map((alias) => ({ tone, alias: normalize(alias) })),
  ).sort((a, b) => b.alias.length - a.alias.length);

  function findTones(value) {
    const text = normalize(value);
    if (!text) return [];

    const matches = [];
    const occupied = [];

    aliasCandidates.forEach(({ tone, alias }) => {
      let start = text.indexOf(alias);
      while (start !== -1) {
        const end = start + alias.length;
        const overlaps = occupied.some(([from, to]) => start < to && end > from);
        if (!overlaps && !matches.some((match) => match.tone.key === tone.key)) {
          matches.push({ tone, start, end });
          occupied.push([start, end]);
          break;
        }
        start = text.indexOf(alias, start + 1);
      }
    });

    return matches
      .sort((a, b) => a.start - b.start)
      .slice(0, 2)
      .map((match) => match.tone);
  }

  function singleGradient(tone) {
    return `linear-gradient(135deg, ${tone.dark} 0%, ${tone.base} 42%, ${tone.light} 52%, ${tone.base} 62%, ${tone.dark} 100%)`;
  }

  function mixHex(a, b, amount = 0.5) {
    const parse = (value) => value.replace('#', '').match(/.{2}/g).map((part) => parseInt(part, 16));
    const [ar, ag, ab] = parse(a);
    const [br, bg, bb] = parse(b);
    const mix = (x, y) => Math.round(x + (y - x) * amount).toString(16).padStart(2, '0');
    return `#${mix(ar, br)}${mix(ag, bg)}${mix(ab, bb)}`;
  }

  function dualGradient(first, second) {
    // V13: two premium metal accents separated by a dark platinum seam.
    // Used mainly for borders/accent bars, never as a full candy-colored fill.
    return `linear-gradient(105deg, ${first.dark} 0%, ${first.base} 23%, ${first.light} 34%, #24242a 46%, #0b0b0e 50%, #24242a 54%, ${second.light} 66%, ${second.base} 77%, ${second.dark} 100%)`;
  }

  function combinedText(tones) {
    if (tones.length === 1) return tones[0].text;
    return tones.every((tone) => tone.text === '#0f1115' || tone.text === '#0b0b0d' || tone.text === '#171014' || tone.text === '#171207' || tone.text === '#17130a' || tone.text === '#160d07')
      ? '#0b0b0d'
      : '#ffffff';
  }

  function applyTone(element, rawValue) {
    const tones = findTones(rawValue);

    if (!tones.length) {
      element.removeAttribute('data-hardrip-tone');
      element.removeAttribute('data-hardrip-tone-count');
      element.style.removeProperty('--hardrip-tone-fill');
      element.style.removeProperty('--hardrip-tone-base');
      element.style.removeProperty('--hardrip-tone-light');
      element.style.removeProperty('--hardrip-tone-dark');
      element.style.removeProperty('--hardrip-tone-text');
      element.style.removeProperty('--hardrip-tone-shadow');
      element.style.removeProperty('--hardrip-tone-one');
      element.style.removeProperty('--hardrip-tone-two');
      return;
    }

    const fill = tones.length > 1 ? dualGradient(tones[0], tones[1]) : singleGradient(tones[0]);
    const shadow = tones.length > 1 ? 'rgba(95, 60, 150, 0.24)' : tones[0].shadow;

    element.dataset.hardripTone = tones.map((tone) => tone.key).join('--');
    element.dataset.hardripToneCount = String(tones.length);
    element.style.setProperty('--hardrip-tone-fill', fill);
    element.style.setProperty('--hardrip-tone-base', tones[0].base);
    element.style.setProperty('--hardrip-tone-light', tones[0].light);
    element.style.setProperty('--hardrip-tone-dark', tones[0].dark);
    element.style.setProperty('--hardrip-tone-text', combinedText(tones));
    element.style.setProperty('--hardrip-tone-shadow', shadow);
    element.style.setProperty('--hardrip-tone-one', tones[0].base);
    element.style.setProperty('--hardrip-tone-two', (tones[1] || tones[0]).base);
  }

  function updateDropdown(select) {
    const wrapper = select.closest('.product-form__input--hardrip-dropdown');
    if (!wrapper) return;
    applyTone(wrapper, select.options[select.selectedIndex]?.value || select.value);
  }

  const shineAnimations = new WeakMap();
  const pickerSchedules = new WeakMap();
  const tooltipTimers = new WeakMap();

  function clearTooltip(label) {
    const state = tooltipTimers.get(label);
    if (state?.hide) window.clearTimeout(state.hide);
    if (state?.finish) window.clearTimeout(state.finish);
    tooltipTimers.delete(label);
    label?.classList.remove('hardrip-tooltip-active', 'hardrip-tooltip-hiding');
  }

  function showTemporaryTooltip(label) {
    if (!(label instanceof HTMLElement) || !label.classList.contains('hardrip-image-swatch')) return;
    if (!window.matchMedia('(hover: none)').matches) return;

    document
      .querySelectorAll('.hardrip-image-swatch.hardrip-tooltip-active, .hardrip-image-swatch.hardrip-tooltip-hiding')
      .forEach((other) => {
        if (other !== label) clearTooltip(other);
      });

    clearTooltip(label);
    label.classList.add('hardrip-tooltip-active');

    const hide = window.setTimeout(() => {
      label.classList.remove('hardrip-tooltip-active');
      label.classList.add('hardrip-tooltip-hiding');

      const finish = window.setTimeout(() => clearTooltip(label), 360);
      tooltipTimers.set(label, { hide: null, finish });
    }, 1350);

    tooltipTimers.set(label, { hide, finish: null });
  }

  function triggerLuxuryShine(label, options = {}) {
    if (!(label instanceof HTMLElement) || !label.matches('.hardrip-image-swatch, .hardrip-pill')) return;

    const beam = label.querySelector(':scope > .hardrip-variant-shine > .hardrip-variant-beam');
    if (!(beam instanceof HTMLElement) || typeof beam.animate !== 'function') return;

    const previous = shineAnimations.get(label);
    previous?.cancel?.();

    const soft = Boolean(options.soft);
    const duration = soft ? 1580 : 1320;
    const maxOpacity = soft ? 0.76 : 0.96;

    const animation = beam.animate(
      [
        { transform: 'translate3d(-250%, 0, 0) rotate(17deg)', opacity: 0, offset: 0 },
        { opacity: 0.10, offset: 0.08 },
        { opacity: maxOpacity, offset: 0.40 },
        { opacity: soft ? 0.44 : 0.58, offset: 0.68 },
        { transform: 'translate3d(430%, 0, 0) rotate(17deg)', opacity: 0, offset: 1 },
      ],
      {
        duration,
        easing: 'cubic-bezier(.22,.72,.18,1)',
        fill: 'none',
      },
    );

    shineAnimations.set(label, animation);
    animation.finished
      .catch(() => {})
      .finally(() => {
        if (shineAnimations.get(label) === animation) shineAnimations.delete(label);
      });
  }

  function selectedLabels(picker) {
    return Array.from(picker.querySelectorAll('.hardrip-option-input:checked'))
      .map((input) => input.nextElementSibling)
      .filter((label) => label?.matches?.('.hardrip-image-swatch, .hardrip-pill'));
  }

  function playSelectedShines(picker) {
    if (!picker?.isConnected || document.hidden) return;

    selectedLabels(picker).forEach((label, index) => {
      window.setTimeout(() => {
        if (label.isConnected) triggerLuxuryShine(label, { soft: true });
      }, index * 160);
    });
  }

  function clearPickerSchedule(picker) {
    const state = pickerSchedules.get(picker);
    if (state?.first) window.clearTimeout(state.first);
    if (state?.interval) window.clearInterval(state.interval);
    pickerSchedules.delete(picker);
  }

  function startPeriodicShine(picker) {
    if (!picker) return;

    clearPickerSchedule(picker);
    const first = window.setTimeout(() => playSelectedShines(picker), 4800);
    const interval = window.setInterval(() => playSelectedShines(picker), 10400);
    pickerSchedules.set(picker, { first, interval });
  }

  function findLabelByValue(scope, rawValue) {
    const target = normalize(rawValue);
    const labels = scope?.querySelectorAll?.('[data-hardrip-variant-label]') || [];

    return (
      Array.from(labels).find((label) => normalize(label.dataset.hardripValue || label.textContent) === target) || null
    );
  }

  function replayAfterShopifyRefresh(productScope, rawValue, originalLabel, showTooltip) {
    let attempt = 0;

    const replay = () => {
      attempt += 1;
      const picker = productScope?.querySelector?.(SELECTOR) || document.querySelector(SELECTOR);
      if (!picker) {
        if (attempt < 4) window.setTimeout(replay, 90);
        return;
      }

      const label = findLabelByValue(picker, rawValue) || selectedLabels(picker).at(-1) || originalLabel;
      if (!label?.isConnected) {
        if (attempt < 4) window.setTimeout(replay, 90);
        return;
      }

      triggerLuxuryShine(label);
      if (showTooltip && label.classList.contains('hardrip-image-swatch')) showTemporaryTooltip(label);
      startPeriodicShine(picker);
    };

    window.setTimeout(replay, 100);
  }

  function initializePicker(picker) {
    picker.querySelectorAll('[data-hardrip-variant-label]').forEach((label) => {
      applyTone(label, label.dataset.hardripValue || label.textContent);
    });

    picker.querySelectorAll('.product-form__input--hardrip-dropdown .select__select').forEach(updateDropdown);
    startPeriodicShine(picker);
  }

  function initialize(root = document) {
    if (root.matches?.(SELECTOR)) initializePicker(root);
    root.querySelectorAll?.(SELECTOR).forEach(initializePicker);
  }

  let lastPressedLabel = null;

  document.addEventListener(
    'pointerdown',
    (event) => {
      const label = event.target.closest?.(
        '.hardrip-variant-picker .hardrip-image-swatch, .hardrip-variant-picker .hardrip-pill'
      );
      if (!label) return;

      const inputId = label.getAttribute('for');
      const input = inputId ? document.getElementById(inputId) : null;
      if (input?.disabled || input?.classList.contains('visually-disabled') || input?.classList.contains('disabled')) {
        return;
      }

      lastPressedLabel = label;
      triggerLuxuryShine(label);
      if (label.classList.contains('hardrip-image-swatch')) showTemporaryTooltip(label);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      if (event.detail !== 0) return;
      const label = event.target.closest?.(
        '.hardrip-variant-picker .hardrip-image-swatch, .hardrip-variant-picker .hardrip-pill'
      );
      if (label) triggerLuxuryShine(label);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType === 'touch') return;
      const label = event.target.closest?.(
        '.hardrip-variant-picker .hardrip-image-swatch, .hardrip-variant-picker .hardrip-pill'
      );
      if (!label || label.contains(event.relatedTarget)) return;
      triggerLuxuryShine(label, { soft: true });
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'change',
    (event) => {
      if (event.target.matches('.product-form__input--hardrip-dropdown .select__select')) updateDropdown(event.target);

      if (event.target.matches('.hardrip-variant-picker .hardrip-option-input')) {
        const productScope = event.target.closest('product-info, .product, .shopify-section') || document;
        replayAfterShopifyRefresh(productScope, event.target.value, lastPressedLabel, true);
      }
    },
    true
  );

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) document.querySelectorAll(SELECTOR).forEach(startPeriodicShine);
  });

  window.addEventListener('pageshow', () => {
    document
      .querySelectorAll('.hardrip-image-swatch.hardrip-tooltip-active, .hardrip-image-swatch.hardrip-tooltip-hiding')
      .forEach(clearTooltip);
    document.querySelectorAll(SELECTOR).forEach(startPeriodicShine);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) initialize(node);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
